import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {NgModule} from '@angular/core';
import {initializeApp, provideFirebaseApp} from '@angular/fire/app';
import {getAuth, provideAuth} from '@angular/fire/auth';
import {AngularFireModule} from '@angular/fire/compat';
import {AngularFireAuthModule} from '@angular/fire/compat/auth';
import {AngularFireDatabaseModule} from '@angular/fire/compat/database';
import {AngularFirestoreModule} from '@angular/fire/compat/firestore';
import {getDatabase, provideDatabase} from '@angular/fire/database';
import {getFirestore, provideFirestore} from '@angular/fire/firestore';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import {provideEffectsManager} from '@ngneat/effects-ng';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';

import {
  AuthInterceptor,
  CachedHttpTranslationLoader,
  ConfigService,
  CoreModule,
  constants,
  enableKendoTheming,
  provideAkitaDevtools,
  provideAppInitializers,
  provideMaterialSettings
} from '@q9elements/ui-kit/common';
import {LoadingPlaceholderModule} from '@q9elements/ui-kit/components';

import {IntercomModule} from 'ng-intercom';
import {CookieService} from 'ngx-cookie-service';
import {NgxLocalStorageModule} from 'ngx-localstorage';

import {environment} from '../environments/environment';
/** Components */
import {FirebaseAbstractService} from '@q9elements/ui-kit/refmodels';
import {AppComponent} from './app.component';
import {appRoutes} from './app.routes';
import {CodeSearchModule} from './code-search/code-search.module';
import {RMECoreModule} from './core/core.module';
import {AuthGuard} from './core/guards/auth.guard';
import {ImpactAssessmentResolve} from './core/resolvers/impact-assessment.resolver';
import {LicenseResolve} from './core/resolvers/license.resolver';
import {NodePathResolve} from './core/resolvers/node-path.resolver';
import {RefModelMetaResolve} from './core/resolvers/ref-model-meta.resolver';
import {SfAnalyticsResolve} from './core/resolvers/sf-analytics.resolver';
import {UserResolve} from './core/resolvers/user.resolver';
import {FirebaseService} from './core/services/firebase.service';
import {EditorInterfaceModule} from './editor-interface/editor-interface.module';
import {ImpactAssessmentModule} from './impact-assessment/impact-assessment.module';
import {ImpactGridViewModule} from './impact-grid-view/impact-grid-view.module';
import {SharedModule} from './shared/shared.module';

/** Services */

/** Routing */

/** App modules */

ConfigService.setConfig({environment, appName: 'refModel'});

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(appRoutes),
    AngularFireModule.initializeApp(environment.fbConfig),
    provideFirebaseApp(() => initializeApp(environment.fbConfig)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideDatabase(() => getDatabase()),
    AngularFireAuthModule,
    AngularFireDatabaseModule,
    AngularFirestoreModule,
    EditorInterfaceModule,
    // Will be removed in future releases (SfAnalyticsModule is deprecated)
    // SfAnalyticsModule,
    ImpactAssessmentModule,
    ImpactGridViewModule,
    CoreModule,
    RMECoreModule,
    SharedModule,
    NgxLocalStorageModule.forRoot({prefix: constants.localStorage.prefix}),
    CodeSearchModule,
    TranslateModule.forRoot({
      // defaultLanguage: 'en',
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useClass: CachedHttpTranslationLoader
      }
    }),
    IntercomModule.forRoot({appId: environment.INTERCOM_APP_ID, updateOnRouterChange: true}),
    LoadingPlaceholderModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    AuthGuard,
    CookieService,
    UserResolve,
    LicenseResolve,
    NodePathResolve,
    RefModelMetaResolve,
    SfAnalyticsResolve,
    ImpactAssessmentResolve,
    !environment.production ? provideAkitaDevtools() : [],
    provideAppInitializers(),
    provideEffectsManager(),
    provideMaterialSettings(),
    enableKendoTheming(true),
    {provide: FirebaseAbstractService, useClass: FirebaseService}
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
